import React, { useState, useRef, useEffect } from 'react';
import { 
  Keyboard, BookOpen, BarChart2, Search, ShoppingCart, FileSpreadsheet,
  Monitor, Package, Target, Sparkles, Play, CheckCircle, ChevronDown, ChevronUp, Zap, ArrowRight, Star
} from 'lucide-react';

const modules = [
  { 
    id: 1, icon: Keyboard, title: "Базовые навыки", lessons: 6, color: "blue",
    description: "Работа с компьютером и таблицами",
    topics: ["Горячие клавиши", "Google Таблицы", "Excel базовый", "Работа с файлами", "Организация данных", "Скорость работы"]
  },
  { 
    id: 2, icon: BookOpen, title: "Фундамент Amazon", lessons: 7, color: "emerald",
    description: "Как устроена платформа",
    topics: ["Терминология", "Модели: FBA, FBM, Wholesale", "Структура листинга", "Комиссии Amazon", "Требования к продавцам", "Buy Box", "Категории"]
  },
  { 
    id: 3, icon: BarChart2, title: "Keepa", lessons: 5, color: "violet",
    description: "Анализ рынка и истории товаров",
    topics: ["Чтение графиков", "История цен", "Анализ конкурентов", "Фильтры поиска", "Оценка спроса"]
  },
  { 
    id: 4, icon: Search, title: "Поиск товаров", lessons: 9, color: "amber",
    description: "Где и как искать прибыльные товары",
    topics: ["Онлайн арбитраж", "Софт для поиска", "Купоны и кэшбэки", "Списки магазинов", "Критерии отбора", "ROI расчёт", "Риски", "Поиск для EU", "Практика"]
  },
  { 
    id: 5, icon: ShoppingCart, title: "Поставщики", lessons: 8, color: "rose",
    description: "Поиск поставщиков и переговоры",
    topics: ["Поиск через Google", "Поиск через ИИ", "Первое письмо", "Переговоры", "Условия оплаты", "Скидки", "Парсинг контактов", "Долгосрочные отношения"]
  },
  { 
    id: 6, icon: FileSpreadsheet, title: "Прайс-листы", lessons: 6, color: "cyan",
    description: "Обработка и анализ прайсов",
    topics: ["Keepa + Excel", "Софт для обработки", "Товарные пары", "Фильтрация", "Оформление", "Автоматизация"]
  },
  { 
    id: 7, icon: Monitor, title: "Seller Central", lessons: 13, color: "violet",
    description: "Работа в кабинете продавца",
    topics: ["Обзор кабинета", "Добавление товара", "Account Health", "Финансы", "Инвентарь", "Отчёты", "Поддержка Amazon"]
  },
  { 
    id: 8, icon: Target, title: "Анализ товаров", lessons: 9, color: "amber",
    description: "Проверка перед закупкой",
    topics: ["Проверка продаж", "Хазмат", "Buy Box анализ", "Юнит-экономика", "Чек-лист", "Риски", "Конкуренты", "Сезонность", "Тренды"]
  },
  { 
    id: 9, icon: Package, title: "Логистика", lessons: 6, color: "emerald",
    description: "Доставка товара на склад Amazon",
    topics: ["Преп-центры", "Создание шипмента", "Sellerboard", "FBM доставка", "Налоги", "Документы"]
  },
  { 
    id: 10, icon: Sparkles, title: "Бонусы", lessons: 6, color: "blue",
    description: "Дополнительные инструменты",
    topics: ["Шаблоны таблиц", "Ангейтинг", "Репрайсер", "Adobe Acrobat", "Полезные контакты", "Чек-листы"]
  },
];

const colorStyles = {
  blue: { gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-500/10', text: 'text-blue-400', border: 'border-blue-500/40', shadow: 'shadow-blue-500/30', glow: 'bg-blue-500/20' },
  emerald: { gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/40', shadow: 'shadow-emerald-500/30', glow: 'bg-emerald-500/20' },
  violet: { gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/40', shadow: 'shadow-violet-500/30', glow: 'bg-violet-500/20' },
  amber: { gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/40', shadow: 'shadow-amber-500/30', glow: 'bg-amber-500/20' },
  rose: { gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-500/10', text: 'text-rose-400', border: 'border-rose-500/40', shadow: 'shadow-rose-500/30', glow: 'bg-rose-500/20' },
  cyan: { gradient: 'from-cyan-500 to-teal-600', bg: 'bg-cyan-500/10', text: 'text-cyan-400', border: 'border-cyan-500/40', shadow: 'shadow-cyan-500/30', glow: 'bg-cyan-500/20' },
};

const ModuleItem = ({ module, isOpen, onToggle, index }) => {
  const Icon = module.icon;
  const colors = colorStyles[module.color];
  const contentRef = useRef(null);
  const [contentHeight, setContentHeight] = useState(0);

  useEffect(() => {
    if (contentRef.current) setContentHeight(contentRef.current.scrollHeight);
  }, [isOpen]);

  return (
    <div 
      data-testid={`module-item-${module.id}`}
      className="group"
      style={{ animationDelay: `${index * 30}ms` }}
    >
      {/* Card */}
      <div className={`relative rounded-2xl border-2 overflow-hidden transition-all duration-500 ${
        isOpen 
          ? `${colors.bg} ${colors.border} shadow-2xl ${colors.shadow}` 
          : 'bg-zinc-900/40 border-zinc-800/60 hover:border-zinc-700 hover:bg-zinc-900/60'
      }`}>
        
        {/* Glow effect when open */}
        {isOpen && (
          <div className={`absolute -inset-2 ${colors.glow} rounded-3xl blur-2xl -z-10 animate-pulse`} />
        )}
        
        {/* Top accent line */}
        <div className={`h-1 bg-gradient-to-r ${colors.gradient} transition-transform duration-500 origin-left ${isOpen ? 'scale-x-100' : 'scale-x-0'}`} />
        
        {/* Header */}
        <button 
          onClick={onToggle}
          data-testid={`module-toggle-${module.id}`}
          className="w-full p-4 sm:p-5 cursor-pointer select-none text-left"
          style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
        >
          <div className="flex items-start sm:items-center gap-3 sm:gap-4">
            {/* Number with gradient */}
            <div className={`relative w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center font-black text-base sm:text-xl bg-gradient-to-br ${colors.gradient} text-white flex-shrink-0 transition-all duration-500 ${isOpen ? 'scale-110 rotate-3' : 'group-hover:scale-105'}`}>
              {module.id}
              {isOpen && <div className={`absolute inset-0 rounded-xl sm:rounded-2xl bg-gradient-to-br ${colors.gradient} animate-ping opacity-30`} />}
            </div>
            
            {/* Text + Badge container */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start sm:items-center gap-2 sm:gap-3 flex-wrap sm:flex-nowrap">
                {/* Icon - hidden on mobile, visible on sm+ */}
                <div className={`hidden sm:flex w-12 h-12 rounded-xl items-center justify-center ${colors.bg} flex-shrink-0 transition-all duration-300 ${isOpen ? `ring-2 ring-offset-2 ring-offset-zinc-900 ${colors.border}` : ''}`}>
                  <Icon className={`w-6 h-6 ${colors.text} transition-transform duration-300 ${isOpen ? 'scale-110' : ''}`} />
                </div>
                
                {/* Title and description */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className={`font-black text-base sm:text-xl transition-colors duration-300 ${isOpen ? 'text-white' : 'text-zinc-200'}`}>
                      {module.title}
                    </h4>
                    {/* Badge - inline on mobile */}
                    <div className={`px-2 py-1 sm:px-3 sm:py-1.5 rounded-full text-xs sm:text-sm font-bold transition-all duration-300 ${isOpen ? `${colors.bg} ${colors.text}` : 'bg-zinc-800 text-zinc-400'}`}>
                      {module.lessons} уроков
                    </div>
                  </div>
                  <p className={`text-xs sm:text-sm transition-colors duration-300 mt-0.5 ${isOpen ? colors.text : 'text-zinc-500'}`}>
                    {module.description}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Chevron */}
            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg sm:rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-500 ${isOpen ? `bg-gradient-to-br ${colors.gradient}` : 'bg-zinc-800'}`}>
              <ChevronDown className={`w-4 h-4 sm:w-5 sm:h-5 transition-all duration-500 ${isOpen ? 'text-white rotate-180' : 'text-zinc-500'}`} />
            </div>
          </div>
        </button>
        
        {/* Content */}
        <div 
          className="overflow-hidden transition-all duration-500 ease-out"
          style={{ maxHeight: isOpen ? `${contentHeight}px` : '0px', opacity: isOpen ? 1 : 0 }}
        >
          <div ref={contentRef} className="px-4 sm:px-5 pb-4 sm:pb-5">
            <div className="pt-3 sm:pt-4 border-t border-zinc-800/50">
              <div className="flex items-center gap-2 mb-3 sm:mb-4">
                <Play className={`w-3 h-3 sm:w-4 sm:h-4 ${colors.text}`} />
                <span className={`text-xs sm:text-sm font-bold uppercase tracking-wider ${colors.text}`}>Темы уроков</span>
              </div>
              <div className="flex flex-wrap gap-1.5 sm:gap-2">
                {module.topics.map((topic, i) => (
                  <div 
                    key={i} 
                    className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2.5 rounded-lg sm:rounded-xl bg-zinc-800/80 text-zinc-100 text-xs sm:text-sm font-medium border border-zinc-700/50 hover:border-zinc-600 transition-all duration-300 hover:-translate-y-0.5`}
                    style={{ animationDelay: `${i * 50}ms` }}
                  >
                    <div className={`w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full bg-gradient-to-r ${colors.gradient} flex-shrink-0`} />
                    <span className="whitespace-nowrap">{topic}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ModulesSection = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [openModuleId, setOpenModuleId] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const totalLessons = modules.reduce((sum, m) => sum + m.lessons, 0);

  return (
    <section ref={sectionRef} className="relative py-20 sm:py-28 overflow-hidden bg-[#030304]" id="modules">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-blue-500/[0.03] rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-violet-500/[0.03] rounded-full blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className={`text-center mb-12 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-500/30 mb-6 backdrop-blur-xl">
            <BookOpen className="w-4 h-4 text-blue-400" />
            <span className="text-blue-300 font-bold text-sm tracking-wide">ПРОГРАММА КУРСА</span>
          </div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black mb-4">
            <span className="text-white">Модули </span>
            <span className="bg-gradient-to-r from-blue-400 via-indigo-400 to-blue-400 bg-clip-text text-transparent">курса</span>
          </h2>
          <p className="text-xl text-zinc-400">10 модулей • {totalLessons} уроков • От старта до продаж</p>
        </div>

        {/* Main Toggle - MEGA */}
        <div className={`transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="relative group">
            {/* Glow */}
            <div className={`absolute -inset-2 bg-gradient-to-r from-blue-500/30 via-indigo-500/30 to-blue-500/30 rounded-3xl blur-xl transition-opacity duration-500 ${isExpanded ? 'opacity-100' : 'opacity-0 group-hover:opacity-50'}`} />
            
            <button 
              onClick={() => setIsExpanded(!isExpanded)}
              data-testid="modules-main-toggle"
              className={`relative w-full flex items-center justify-between gap-6 p-6 sm:p-8 rounded-3xl border-2 cursor-pointer select-none transition-all duration-500 ${
                isExpanded 
                  ? 'bg-gradient-to-br from-blue-500/15 via-indigo-500/10 to-blue-500/15 border-blue-500/50 shadow-2xl shadow-blue-500/20' 
                  : 'bg-zinc-900/60 border-zinc-800 hover:border-zinc-700 hover:bg-zinc-900/80'
              }`}
              style={{ touchAction: 'manipulation' }}
            >
              <div className="flex items-center gap-5">
                <div className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center transition-all duration-500 ${
                  isExpanded ? 'bg-gradient-to-br from-blue-500 to-indigo-600 rotate-3' : 'bg-zinc-800 group-hover:bg-zinc-700'
                }`}>
                  <BookOpen className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
                  {isExpanded && <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 animate-pulse opacity-50" />}
                </div>
                
                <div className="text-left">
                  <h3 className="text-2xl sm:text-3xl font-black text-white mb-1">10 модулей</h3>
                  <p className={`text-lg font-semibold transition-colors duration-300 ${isExpanded ? 'text-blue-400' : 'text-zinc-500'}`}>
                    {totalLessons} уроков • Доступ навсегда
                  </p>
                </div>
              </div>
              
              <div className={`flex items-center gap-3 px-6 py-4 rounded-2xl font-black text-lg transition-all duration-500 ${
                isExpanded 
                  ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg shadow-blue-500/30' 
                  : 'bg-zinc-800 text-zinc-300 group-hover:bg-zinc-700'
              }`}>
                <span className="hidden sm:inline">{isExpanded ? 'Скрыть' : 'Раскрыть программу'}</span>
                <ChevronDown className={`w-6 h-6 transition-transform duration-500 ${isExpanded ? 'rotate-180' : ''}`} />
              </div>
            </button>
          </div>
        </div>

        {/* Modules List */}
        <div className={`overflow-hidden transition-all duration-700 ${isExpanded ? 'max-h-[8000px] opacity-100 mt-6' : 'max-h-0 opacity-0 mt-0'}`}>
          <div className="space-y-3">
            {modules.map((module, index) => (
              <ModuleItem 
                key={module.id}
                module={module}
                index={index}
                isOpen={openModuleId === module.id}
                onToggle={() => setOpenModuleId(openModuleId === module.id ? null : module.id)}
              />
            ))}
            
            {/* Footer */}
            <div className="flex items-center justify-center gap-4 p-6 rounded-2xl bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 mt-6">
              <CheckCircle className="w-6 h-6 text-emerald-400" />
              <span className="text-emerald-300 font-bold text-lg">Доступ ко всем модулям навсегда</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ModulesSection;
